import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import WaveSurfer from "wavesurfer.js";
import { formatWallClock, formatMeetingDate } from "../lib/timestamps";

export interface AudioPlayerHandle {
  seekToMs: (ms: number) => void;
  play: () => void;
  // pause() lets parent components freeze playback when an inline-edit
  // popover opens, so the highlight-turn slice's active-turn-change
  // auto-scroll doesn't race the user's edit (DECISIONS.md 2026-05-13
  // "Correction UI" entry, pause-on-inline-edit principle).
  pause: () => void;
}

interface Props {
  url: string;
  // Drives the playhead readout. When null, the readout falls back to
  // relative M:SS via formatWallClock (same helper transcript turns use).
  recordedAt: string | null;
  // Optional fan-out for the playback time. Fired from the same two
  // wavesurfer subscriptions that drive the readout (audioprocess +
  // seeking). Consumers (e.g. TranscriptView's highlight-active-turn)
  // should write into a ref, NOT React state -- this fires ~20x/sec
  // during playback and parent re-renders are wasteful. AudioPlayer
  // itself keeps an internal useState for the readout because nothing
  // else in this component reconciles on it.
  onTimeUpdate?: (ms: number) => void;
  // Playback speed (1 = normal). Owned by the parent (PlayerDock's speed
  // select); applied to the wavesurfer instance via setPlaybackRate both
  // on change AND on (re)create, so the chosen speed survives the
  // collapse/expand remount of the dock.
  playbackRate?: number;
}

export const AudioPlayer = forwardRef<AudioPlayerHandle, Props>(
  ({ url, recordedAt, onTimeUpdate, playbackRate = 1 }, ref) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  // currentTimeMs lives inside AudioPlayer so only this component
  // reconciles on audioprocess (fires ~20x/sec during playback).
  // Parent (MeetingReview) never re-renders during playback.
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  // Latest onTimeUpdate captured in a ref so we don't have to tear
  // down + rebuild the wavesurfer instance when the parent passes a
  // new callback identity on re-render. The effect below depends on
  // [url] only; reading through the ref always sees the current fn.
  const onTimeUpdateRef = useRef(onTimeUpdate);
  useEffect(() => {
    onTimeUpdateRef.current = onTimeUpdate;
  }, [onTimeUpdate]);
  // Same ref pattern for playbackRate: the create effect below depends on
  // [url] only, so it reads the current rate through this ref when the
  // instance is (re)built; the change effect applies live updates.
  const playbackRateRef = useRef(playbackRate);
  useEffect(() => {
    playbackRateRef.current = playbackRate;
    wavesurferRef.current?.setPlaybackRate(playbackRate);
  }, [playbackRate]);

  useEffect(() => {
    if (!containerRef.current) return;
    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: "#475569",
      progressColor: "#22d3ee",
      cursorColor: "#f9fafb",
      height: 80,
      barWidth: 2,
      barGap: 1,
      url,
    });
    wavesurferRef.current = ws;
    // audioprocess fires during playback; seeking fires on manual seeks
    // (including the click-to-scrub from TranscriptView). Both keep the
    // readout in sync AND fan the time out via onTimeUpdate so the
    // transcript can highlight the active turn. ws.destroy() in the
    // cleanup below removes every listener attached via ws.on() -- no
    // manual unsubscribe needed.
    const emit = (t: number) => {
      const ms = t * 1000;
      setCurrentTimeMs(ms);
      onTimeUpdateRef.current?.(ms);
    };
    ws.on("audioprocess", emit);
    ws.on("seeking", emit);
    // Re-apply the parent-owned playback rate on (re)create so the chosen
    // speed survives a dock collapse/expand or a url refresh.
    if (playbackRateRef.current !== 1) {
      ws.setPlaybackRate(playbackRateRef.current);
    }
    return () => {
      ws.destroy();
      wavesurferRef.current = null;
    };
  }, [url]);

  useImperativeHandle(
    ref,
    () => ({
      seekToMs(ms: number) {
        const ws = wavesurferRef.current;
        if (!ws) return;
        const dur = ws.getDuration();
        if (!dur) return;
        ws.setTime(ms / 1000);
      },
      play() {
        wavesurferRef.current?.play();
      },
      pause() {
        wavesurferRef.current?.pause();
      },
    }),
    [],
  );

  return (
    <div
      className="card"
      style={{
        // Studio layout: AudioPlayer lives inside the bottom PlayerDock
        // (pages/meeting/PlayerDock.tsx), which owns the sticky-bottom
        // positioning. The old sticky-top + --sticky-header-h offset that
        // pinned this card under the title row is gone with that move;
        // --sticky-header-h still drives .turn scroll-margin-top and is
        // kept in sync by MeetingHeader's ResizeObserver.
        background: "var(--surface)",
      }}
    >
      <div ref={containerRef} />
      <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>
        <button type="button" onClick={() => wavesurferRef.current?.playPause()}>
          Play / pause
        </button>
        <span
          style={{
            marginInlineStart: "auto",
            display: "inline-flex",
            alignItems: "baseline",
            gap: 8,
          }}
        >
          {recordedAt && (
            <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
              {formatMeetingDate(recordedAt)}
            </span>
          )}
          <span
            style={{ fontFamily: "monospace", fontSize: 14, color: "var(--text-muted)" }}
            aria-label="Playhead time"
          >
            {formatWallClock(recordedAt, currentTimeMs)}
          </span>
        </span>
      </div>
    </div>
  );
});

AudioPlayer.displayName = "AudioPlayer";
